import { useNavigate } from 'react-router-dom'
import { Search, Megaphone, PenSquare } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type QuickAction = {
  readonly label: string
  readonly description: string
  readonly icon: typeof Search
  readonly path: string
  readonly iconColor: string
  /** Visually emphasized row — the action we want new users to take first. */
  readonly primary?: boolean
}

// Campaign-first mental model (owner directive, 2026-08-31): the campaign is
// the container everything else hangs off, and its wizard now has an in-line
// "Add leads" path, so creating a campaign is the primary nudge and finding
// leads the secondary one. Both remain one click away.
const QUICK_ACTIONS: readonly QuickAction[] = [
  {
    label: 'New Campaign',
    description: 'Start here: create an outreach campaign',
    icon: Megaphone,
    path: '/campaigns',
    iconColor: 'text-purple-500 bg-purple-500/10',
    primary: true,
  },
  {
    label: 'Find Leads',
    description: 'Search and discover new prospects',
    icon: Search,
    // Land directly on the lead finder — '/leads' is the pipeline view,
    // which has nothing to search on a fresh account.
    path: '/leads/discover',
    iconColor: 'text-blue-500 bg-blue-500/10',
  },
  {
    label: 'Create Post',
    description: 'Draft LinkedIn content',
    icon: PenSquare,
    // Deep-link to the Content page with the generate-post dialog auto-opened.
    // The dashboard's customer routes don't have a `/content/new` route — the
    // old path landed on the catch-all 404. content-page.tsx reads `?new=1`
    // and fires setGenerateOpen(true) on mount.
    path: '/content?new=1',
    iconColor: 'text-emerald-500 bg-emerald-500/10',
  },
]

export function QuickActions() {
  const navigate = useNavigate()

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Actions</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon
          const colorClasses = action.iconColor.split(' ')

          return (
            <button
              key={action.label}
              type="button"
              onClick={() => navigate(action.path)}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg p-3 text-left',
                'hover:bg-accent transition-colors',
                // Primary row reads as THE next step without shouting.
                action.primary && 'border border-primary/30 bg-primary/10',
              )}
            >
              <div
                className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                  colorClasses[1],
                )}
              >
                <Icon className={cn('h-5 w-5', colorClasses[0])} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">{action.label}</p>
                <p className="text-xs text-muted-foreground">{action.description}</p>
              </div>
            </button>
          )
        })}
      </CardContent>
    </Card>
  )
}
